import { useContext, useState, Fragment, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MobXProviderContext } from "mobx-react";
import classNames from "classnames";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { RadioSelector } from "components";
import { getDayName } from "utils/helpers/dateTime";
import { preventMinus } from "utils/helpers/validation";
import { priceFormatter } from "utils/helpers/validation";

function ChangeSubsidy({
  visible,
  setModal,
  bulkUserUpdate = false,
  bulkUsers,
  updatingMeal = false,
  meal,
  updatingUserSubsidiesForMeal,
  onComplete,
  handleApplyPressed,
}) {
  const [subsidyType, setSubsidyType] = useState("daily");
  const [weeklySubsidy, setWeeklySubsidy] = useState(0);
  const [dailySubsidy, setDailySubsidy] = useState("");
  const [mealSeriesStandardSubsidy, setMealSeriesStandardSubsidy] =
    useState("");
  const [overPayment, setOverPayment] = useState(true);
  const [inputError, setInputError] = useState(false);
  const { mealManagementStore } = useContext(MobXProviderContext);
  const mealPlan = mealManagementStore.mealPlan;
  const { homeStore } = useContext(MobXProviderContext);
  const { userStore } = useContext(MobXProviderContext);
  const { locationID } = userStore;
  const [customSubsidyValue, setCustomSubsidyValue] = useState("");
  const [applySubsidyTo, setApplySubsidyTo] = useState("1");
  const [mealPlanSubsidyType, setMealPlanSubsidyType] = useState("daily");
  const [mealPlanDailySubsidy, setMealPlanDailySubsidy] = useState("");
  const [mealPlanWeeklySubsidy, setMealPlanWeeklySubsidy] = useState(0);

  useEffect(() => {
    if (!bulkUserUpdate) {
      if (updatingMeal) {
        const rawSubsidy = toJS(meal?.attributes?.daily_subsidy) || {};
        const formattedSubsidy = Object.fromEntries(
          Object.entries(rawSubsidy)?.map(([day, value]) => [
            day,
            parseFloat(value).toFixed(2),
          ])
        );
        const dayName = getDayName(meal?.attributes?.meal_date);

        let subsidy;

        subsidy =
          typeof meal?.attributes?.daily_subsidy == "object"
            ? meal?.attributes?.daily_subsidy?.[dayName] ?? 0
            : meal?.attributes?.daily_subsidy ?? 0;

        setDailySubsidy(priceFormatter(subsidy));
        setSubsidyType(meal?.attributes?.subsidy_type);
        setWeeklySubsidy(priceFormatter(meal?.attributes?.subsidy));
        setMealSeriesStandardSubsidy(
          priceFormatter(meal?.attributes?.meal_series_standard_subsidy)
        );

        let mealPlanSubsidy;

        mealPlanSubsidy =
          typeof meal?.attributes?.meal_series_daily_subsidy == "object"
            ? meal?.attributes?.meal_series_daily_subsidy?.[dayName] ?? 0
            : meal?.attributes?.meal_series_daily_subsidy ?? 0;

        setMealPlanDailySubsidy(priceFormatter(mealPlanSubsidy));
        setMealPlanSubsidyType(meal?.attributes?.meal_series_subsidy_type);
        setMealPlanWeeklySubsidy(meal?.attributes?.meal_series_subsidy);

        if (meal?.id) setApplySubsidyTo(meal?.override_subsidy ? "2" : "1");
      }
    } else {
      setDailySubsidy({
        monday: 0.0,
        tuesday: 0.0,
        wednesday: 0.0,
        thursday: 0.0,
        friday: 0.0,
        saturday: 0.0,
        sunday: 0.0,
      });
    }
  }, [mealPlan]);

  const onSavePress = () => {
    if (updatingUserSubsidiesForMeal) {
      homeStore
        .updateEmployeeSubsidyForMeal(
          {
            meal_id: meal?.id,
            location_id: locationID,
          },
          {
            location_employee_ids: bulkUsers?.map((user) => user?.id),
            subsidy_type: subsidyType,
            daily_subsidy: dailySubsidy,
            subsidy: weeklySubsidy,
            // employee_pays_by_credit_card: overPayment,
          }
        )
        .then(() => {
          onComplete();
          handleApplyPressed();
          setModal(false);
        });
    } else if (updatingMeal) {
      if (!customSubsidyValue) {
        setInputError(true);
        return;
      } else {
        setInputError(false);
      }

      homeStore
        .updateMeal(
          meal?.id,
          {
            meal: {
              override_subsidy: applySubsidyTo == "1" ? false : true,
              subsidy: customSubsidyValue,
              // employee_pays_by_credit_card: overPayment,
            },
          },
          locationID
        )
        .then(() => {
          handleApplyPressed();
          setModal(false);
        });
    } else if (bulkUserUpdate) {
      mealManagementStore
        .bulkSubsidyUpdate(locationID, mealPlan?.id, {
          ids: bulkUsers?.map((user) => user?.id),
          employee_meal_plan: {
            subsidy_type: subsidyType,
            daily_subsidy: dailySubsidy,
            subsidy: weeklySubsidy,
          },
        })
        .then(() => {
          handleApplyPressed();
          setModal(false);
        });
    } else
      mealManagementStore
        .updateMealPlan(locationID, mealPlan?.id, {
          meal_series: {
            subsidy_type: subsidyType,
            daily_subsidy: dailySubsidy,
            subsidy: weeklySubsidy,
            employee_pays_by_credit_card: overPayment,
          },
        })
        .then(() => {
          handleApplyPressed();
          setModal(false);
        });
  };

  const onChangeDaily = (value, day) => {
    let daily = dailySubsidy;
    const regex = /^[0-9]*\.?[0-9]*$/;
    if (regex.test(value)) {
      daily[day] = value;

      setDailySubsidy({ ...daily });
    }
  };

  const setEnableOverpayment = (value) => {
    setOverPayment(value);
  };

  const getSubsidy = () => {
    if (!meal?.attributes?.meal_series_subsidy_type) {
      return mealSeriesStandardSubsidy;
    }
    if (meal?.attributes?.subsidy_type == "daily") {
      return dailySubsidy;
    }
    if (meal?.attributes?.subsidy) {
      return weeklySubsidy;
    }
    if (mealPlanSubsidyType == "daily") {
      return mealPlanDailySubsidy;
    }
    if (mealPlanSubsidyType == "weekly") {
      return `$${mealPlanWeeklySubsidy}`;
    }
    return "$0.00";
  };

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-[90]" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center sm:p-4 md:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all">
                <div className="d-row justify-between items-center mb-[24px]">
                  <h2 className="text-2xl font-inter-bold">
                    Custom Meal Subsidy
                  </h2>

                  <button
                    onClick={() => setModal(false)}
                    className="text-dark-gray hover:text-yellow-600"
                    aria-label="Close"
                  >
                    <span className="text-xl">&times;</span>
                  </button>
                </div>

                <div className="d-col py-4 px-2 rounded-lg bg-[#F3F3F3] mb-[11px]">
                  <small className="text-[#2D3958] font-inter-regular text-[16px]">
                    You can add a custom subsidy for this meal that will
                    override the default meal plan subsidy for users on this
                    day. The current meal plan subsidy is set to:
                  </small>

                  <small className="font-inter-semibold text-[#2D3958] text-[16px] mt-1">
                    {subsidyType == "daily" || mealPlanSubsidyType == "daily"
                      ? "Subsidy Per Meal"
                      : mealPlanSubsidyType == "weekly"
                      ? "Weekly"
                      : "Subsidy Per Meal"}
                    :{getSubsidy()}
                  </small>
                </div>

                <div className="border p-[16px] rounded-md border-[#DEE2E6]">
                  <small className="font-inter-regular text-[16px] text-[#566985]">
                    Enter Custom Amount ($)
                  </small>

                  <div className={classNames({ "opacity-20": false })}>
                    <div
                      className={classNames(
                        "focus:outline-none bg-white border py-2 d-row px-5 items-center rounded-lg",
                        {
                          "border-red-500": inputError,
                          "border-[#DEE2E6]": !inputError,
                        }
                      )}
                    >
                      {customSubsidyValue && (
                        <small className="text-[16px] font-inter-medium mr-1">
                          $
                        </small>
                      )}

                      <input
                        autoFocus
                        className="text-[16px] w-full font-inter-medium bg-transparent h-full focus:outline-none text-black"
                        type="number"
                        required
                        onKeyPress={preventMinus}
                        onChange={(e) => {
                          if (e?.target?.value > -1) {
                            setInputError(false);
                            setCustomSubsidyValue(e.target?.value);
                          }
                        }}
                        value={customSubsidyValue}
                        onWheelCapture={(e) => {
                          e.target.blur();
                        }}
                      />
                    </div>

                    <RadioSelector
                      vertical={true}
                      firstLabel="Apply to users in the meal plan who do not have custom subsidies. Users with existing custom subsidies will retain their current values."
                      secondLabel="Apply to all users in the meal plan and override any existing user-level custom subsidies."
                      selectorValue={applySubsidyTo}
                      setSelector={(value) => {
                        setApplySubsidyTo(value);
                      }}
                    />
                  </div>
                </div>

                <div className="d-row w-full items-center mt-[24px] justify-center">
                  <button
                    className="btn-light btn-style w-full py-3 px-5"
                    onClick={() => setModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn-purple btn-style w-full py-3 px-6 ml-3"
                    onClick={onSavePress}
                  >
                    Save
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default observer(ChangeSubsidy);
